import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type {
  AppointmentStatus,
  AppointmentSummary,
} from "../api/types.js";
import { Card } from "../components/Card.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";
import {
  StatusBadge,
  ModalityBadge,
  PatientTypeBadge,
} from "../components/StatusBadge.js";
import { DocumentStatusBadge } from "../components/DocumentStatusBadge.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { formatDateTime, formatDob } from "../lib/format.js";
import { formatPhone } from "../lib/format-phone.js";

type Props = { api: AdminApi };

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "booked", label: "Booked" },
  { value: "pending_calendar_sync", label: "Pending sync" },
  { value: "cancelled", label: "Cancelled" },
];

function isoStartOfDay(d: string): string {
  return `${d}T00:00:00.000Z`;
}

// Same day-based defaults the old page used (today through +30 days).
function defaultRange(): { fromDate: string; toDate: string } {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const monthLater = new Date(today);
  monthLater.setUTCDate(today.getUTCDate() + 30);
  return {
    fromDate: today.toISOString().slice(0, 10),
    toDate: monthLater.toISOString().slice(0, 10),
  };
}

export function AppointmentsPage({ api }: Props) {
  const [range] = useState(defaultRange);
  const [fromDate, setFromDate] = useState(range.fromDate);
  const [toDate, setToDate] = useState(range.toDate);
  const [statusFilter, setStatusFilter] = useState<"all" | AppointmentStatus>(
    "all",
  );
  const [search, setSearch] = useState("");
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["admin", "appointments", fromDate, toDate, statusFilter],
    queryFn: () =>
      api.listAppointments({
        from: isoStartOfDay(fromDate),
        to: isoStartOfDay(toDate),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      }),
  });

  const filtered = useMemo(() => {
    if (!q.data) return [];
    const term = search.trim().toLowerCase();
    if (!term) return q.data;
    return q.data.filter(
      (a) =>
        a.patient_name.toLowerCase().includes(term) ||
        a.public_code.toLowerCase().includes(term),
    );
  }, [q.data, search]);

  function toggleRow(id: string) {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="grid gap-4">
      <Card title="Filters">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <Field
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as typeof statusFilter)
            }
          />
          <Field
            label="Search name or code"
            type="search"
            placeholder="Jane Doe / A7K3"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <Card title={`Appointments (${q.data ? filtered.length : "…"})`}>
        {q.isPending ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : null}
        {q.isError ? (
          <ErrorBanner message="Could not load appointments." />
        ) : null}
        {q.data && filtered.length === 0 ? (
          <p className="text-sm text-slate-600">No appointments in range.</p>
        ) : null}
        {q.data && filtered.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="w-8 py-2" aria-label="Expand" />
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Patient</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">Visit</th>
                  <th className="py-2 pr-3">Modality</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Docs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((a) => (
                  <AppointmentRow
                    key={a.id}
                    appt={a}
                    open={openRows.has(a.id)}
                    onToggle={() => toggleRow(a.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function AppointmentRow({
  appt,
  open,
  onToggle,
}: {
  appt: AppointmentSummary;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="text-sm text-slate-800 hover:bg-slate-50">
        <td className="py-2 align-top">
          <button
            type="button"
            onClick={onToggle}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label={open ? "Collapse row" : "Expand row"}
            aria-expanded={open}
          >
            <span
              aria-hidden="true"
              className={
                "inline-block transition-transform " +
                (open ? "rotate-90" : "")
              }
            >
              ▸
            </span>
          </button>
        </td>
        <td className="py-2 pr-3">{formatDateTime(appt.scheduled_start)}</td>
        <td className="py-2 pr-3">
          <Link
            to={`/appointments/${appt.id}`}
            className="font-medium text-blue-700 hover:underline"
          >
            {appt.patient_name}
          </Link>
        </td>
        <td className="py-2 pr-3 text-slate-700">
          {formatPhone(appt.patient_phone)}
        </td>
        <td className="py-2 pr-3 text-slate-700">{appt.visit_type_name}</td>
        <td className="py-2 pr-3">
          <ModalityBadge value={appt.modality} />
        </td>
        <td className="py-2 pr-3">
          <PatientTypeBadge value={appt.patient_type} />
        </td>
        <td className="py-2 pr-3">
          <StatusBadge status={appt.status} />
        </td>
        <td className="py-2 pr-3">
          <DocumentStatusBadge status={appt.document_status} />
        </td>
      </tr>
      {open ? (
        <tr className="bg-slate-50/60">
          <td />
          <td colSpan={8} className="py-3 pr-3">
            <ExpandedDetails appt={appt} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ExpandedDetails({ appt }: { appt: AppointmentSummary }) {
  return (
    <div className="grid gap-4 text-sm text-slate-700 md:grid-cols-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Contact
        </p>
        <p className="mt-1">{appt.patient_email}</p>
        <p>{formatPhone(appt.patient_phone)}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Patient
        </p>
        <p className="mt-1">DOB: {formatDob(appt.patient_dob)}</p>
        <p>Gender: {genderLabel(appt.patient_gender)}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Address
        </p>
        <p className="mt-1">{appt.patient_address_street1}</p>
        {appt.patient_address_street2 ? (
          <p>{appt.patient_address_street2}</p>
        ) : null}
        <p>
          {appt.patient_address_city}, {appt.patient_address_state}{" "}
          {appt.patient_address_zip}
        </p>
      </div>
      {appt.status === "cancelled" && appt.cancelled_at ? (
        <div className="md:col-span-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900">
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">
            Cancelled
          </p>
          <p className="mt-1">
            {formatDateTime(appt.cancelled_at)} by{" "}
            {appt.cancelled_by ?? "(unknown)"}
          </p>
          {appt.cancel_reason ? (
            <p className="mt-1 italic">"{appt.cancel_reason}"</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function genderLabel(g: AppointmentSummary["patient_gender"]): string {
  switch (g) {
    case "male":
      return "Male";
    case "female":
      return "Female";
    case "nonbinary":
      return "Non-binary";
    case "prefer_not_to_say":
      return "Prefer not to say";
  }
}
